import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useScopedMemberDetail } from '@/hooks/useScopedMemberDetail';
import { formatMessageTime } from '@/lib/chatTime';
import type { RosterSidebarOrg } from './sidebar/RosterSidebar';

const ROLE_LABELS: Record<string, string> = {
  org_admin: 'Org Admin',
  campus_pastor: 'Campus Pastor',
  ministry_director: 'Ministry Director',
  group_leader: 'Leader',
  group_member: 'Member',
};

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-flock-navy-400">{label}</dt>
      <dd className="text-sm text-flock-navy-900">{value}</dd>
    </div>
  );
}

/**
 * Member detail reached from the org-level ("All Members") roster. Basic
 * profile + the member's groups — clicking a group jumps into the full
 * group-context detail (attendance, health, notes), which the leadership
 * bypass on GET /roster/:userId permits even for non-member admins.
 */
export default function ScopedMemberDetailPage({
  org,
  memberId,
}: {
  org: RosterSidebarOrg;
  memberId: string;
}) {
  const navigate = useNavigate();
  const detailQuery = useScopedMemberDetail(org.orgId, memberId);

  if (detailQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
        Loading member…
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
        Couldn&apos;t load this member.
      </div>
    );
  }

  const member = detailQuery.data;
  const displayName =
    member.displayName ||
    [member.firstName, member.lastName].filter(Boolean).join(' ') ||
    member.email ||
    'Member';

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b border-flock-gray-200 bg-white px-6 py-4">
        <button
          onClick={() => navigate(`/roster/org/${org.orgId}`)}
          className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold text-flock-navy-900">{displayName}</h1>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-4">
          <div className="flex items-center gap-4 rounded-2xl border border-flock-gray-200 bg-white p-5 shadow-sm">
            {member.profileImageUrl ? (
              <img
                src={member.profileImageUrl}
                alt=""
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-flock-navy-400 text-xl font-semibold text-white">
                {displayName
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((p) => p[0]?.toUpperCase())
                  .join('')}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-flock-navy-900">{displayName}</div>
              <div className="text-xs text-flock-navy-400">{org.orgName}</div>
              <div className="mt-2 flex gap-2">
                {member.email && (
                  <a
                    href={`mailto:${member.email}`}
                    className="flex items-center gap-1 rounded-lg border border-flock-gray-200 px-2.5 py-1 text-xs text-flock-navy-600 hover:bg-flock-navy-50"
                  >
                    <EnvelopeIcon className="h-3.5 w-3.5" /> Email
                  </a>
                )}
                {member.phone && (
                  <a
                    href={`tel:${member.phone}`}
                    className="flex items-center gap-1 rounded-lg border border-flock-gray-200 px-2.5 py-1 text-xs text-flock-navy-600 hover:bg-flock-navy-50"
                  >
                    <PhoneIcon className="h-3.5 w-3.5" /> Call
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-flock-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-flock-navy-900">Groups</h2>
            {member.groups.length === 0 && (
              <p className="text-sm text-flock-navy-400">Not in any groups yet.</p>
            )}
            <div className="space-y-1">
              {member.groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => navigate(`/roster/group/${group.id}/member/${member.id}`)}
                  className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-flock-navy-50"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-flock-navy-100 text-flock-navy-600">
                    <UserGroupIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-flock-navy-900">
                      {group.name}
                    </div>
                    <div className="truncate text-xs text-flock-navy-400">
                      {ROLE_LABELS[group.role] ?? group.role} · joined{' '}
                      {formatMessageTime(new Date(group.joinedAt))}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-flock-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-flock-navy-900">Contact & Profile</h2>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Email" value={member.email} />
              <Field label="Phone" value={member.phone} />
              <Field
                label="Date of birth"
                value={
                  member.dateOfBirth ? new Date(member.dateOfBirth).toLocaleDateString() : null
                }
              />
              <Field label="Gender" value={member.gender} />
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
